import React from "react";

import { t } from "@auto/services";
import EChart from "@src/reporting/components/EChart";
import ReportingWidget from "@src/reporting/components/ReportingWidget";
import useReportingResource from "@src/reporting/hooks/useReportingResource.mjs";
import { ageLabel, count, sexLabel } from "@src/reporting/model/reportingFormatters.mjs";
import { AGE_LABEL_KEYS } from "@src/reporting/model/reportingConstants.mjs";
import { loadStatsDemographics } from "@src/reporting/services/reportingApi.mjs";
import styles from "@src/reporting/Reporting.module.styl";

const DemographicsWidget = () => {
  const resource = useReportingResource(
    loadStatsDemographics,
    {
      key: "demographics",
      initialData: { sex: [], ageBands: [] },
    },
  );

  const data = resource.data || {};
  const sexRows = (data.sex || []).filter((row) => {
    const category = String(row.category || "").trim().toUpperCase();
    return category !== "PENDING" && category !== "UNKNOWN";
  });
  const ageOrder = Object.keys(AGE_LABEL_KEYS);
  const ageOrderIndex = new Map(ageOrder.map((key, index) => [key, index]));
  const ageRows = (data.ageBands || [])
    .filter((row) => {
      const category = String(row.category || "").trim().toUpperCase();
      return category !== "UNDER_18" && category !== "UNKNOWN";
    })
    .sort((a, b) => {
      const aKey = String(a.category || "").trim().toUpperCase();
      const bKey = String(b.category || "").trim().toUpperCase();
      return (ageOrderIndex.get(aKey) ?? Number.MAX_SAFE_INTEGER)
        - (ageOrderIndex.get(bKey) ?? Number.MAX_SAFE_INTEGER);
    });

  const sexOption = React.useMemo(() => ({
    tooltip: { trigger: "item" },
    legend: { bottom: 0 },
    series: [{
      type: "pie",
      radius: ["42%", "68%"],
      center: ["50%", "43%"],
      avoidLabelOverlap: false,
      label: {
        show: true,
        position: "outside",
        formatter: ({ name, value }) => `${name}: ${count(value)}`,
      },
      labelLine: {
        show: true,
      },
      labelLayout: {
        hideOverlap: false,
      },
      data: sexRows.map((row) => ({
        name: sexLabel(row.category),
        value: Number(row.count || 0),
      })),
    }],
  }), [sexRows]);

  const ageOption = React.useMemo(() => ({
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    grid: { left: 48, right: 14, top: 32, bottom: 54, containLabel: true },
    xAxis: {
      type: "category",
      data: ageRows.map((row) => ageLabel(row.category)),
      axisLabel: { rotate: 28 },
    },
    yAxis: { type: "value", minInterval: 1 },
    series: [{
      type: "bar",
      label: {
        show: true,
        position: "top",
        formatter: ({ value }) => count(value),
      },
      data: ageRows.map((row) => Number(row.count || 0)),
    }],
  }), [ageRows]);

  return (
    <div className={styles.demographicsGrid}>
      <ReportingWidget
        title={t("stats.sexDistribution")}
        loading={resource.loading}
        error={resource.error}
        empty={resource.hasLoaded && sexRows.length === 0}
        emptyMessage={t("stats.noData")}
      >
        <EChart option={sexOption} loading={resource.loading} className={styles.mediumChart} />
      </ReportingWidget>

      <ReportingWidget
        title={t("stats.ageDistribution")}
        loading={resource.loading}
        error={resource.error}
        empty={resource.hasLoaded && ageRows.length === 0}
        emptyMessage={t("stats.noData")}
      >
        <EChart option={ageOption} loading={resource.loading} className={styles.mediumChart} />
      </ReportingWidget>
    </div>
  );
};

export default DemographicsWidget;
